import api from './api';

export interface QueryLogItem {
  id: string;
  at: string;
  toolName: string;
  status: 'success' | 'failed';
  /** Access-control refusal (no grant / AI paused) — flagged distinctly in the UI. */
  denied: boolean;
  errorMessage: string | null;
  rowCount: number | null;
  tookMs: number | null;
  sourceId: string | null;
  sourceName: string | null;
  accountName: string | null;
  initiatedBy: { kind: 'human' | 'routine' | 'anomaly' | 'system'; name: string; sub: string | null };
  via: { kind: 'chat' | 'mcp' | 'automation' | 'admin'; label: string };
  sql: string | null;
  sourceKind: string | null;
}

export interface QueryLogPage {
  items: QueryLogItem[];
  nextCursor: string | null;
  summary: { total: number; failed: number; busiestSource: string | null };
}

/** Which stored column the shown result came from. `summary` → the full result
 *  wasn't retained (gateway tools); the UI offers a live re-run instead. */
export type ResultSource = 'llm_facing' | 'response_data' | 'summary' | null;

export interface QueryLogDetail extends QueryLogItem {
  errorCode: string | null;
  requestPayload: Record<string, unknown> | null;
  result: { value: unknown; source: ResultSource };
  /** True when this tool is in the read-only re-run allow-list. */
  canRerun: boolean;
}

/** Outcome of a live re-run — the fresh execution result (or its error). */
export interface QueryLogRerunResult {
  success: boolean;
  result: unknown;
  error: string | null;
}

export interface QueryLogParams {
  days: number;
  organizationId?: string;
  sourceId?: string;
  userId?: string;
  failedOnly?: boolean;
  cursor?: string;
}

export const queryLogApi = {
  getPage: async (params: QueryLogParams): Promise<QueryLogPage> => {
    const { data } = await api.get('/api/query-log', {
      params: {
        days: params.days,
        organizationId: params.organizationId,
        sourceId: params.sourceId,
        userId: params.userId,
        failedOnly: params.failedOnly ? 'true' : undefined,
        cursor: params.cursor,
      },
    });
    return data.data as QueryLogPage;
  },

  getDetail: async (id: string): Promise<QueryLogDetail> => {
    const { data } = await api.get(`/api/query-log/${id}`);
    return data.data as QueryLogDetail;
  },

  rerun: async (id: string): Promise<QueryLogRerunResult> => {
    const { data } = await api.post(`/api/query-log/${id}/rerun`);
    return data.data as QueryLogRerunResult;
  },
};
